import React, {useEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {KTIcon} from '../../../../_websoft/helpers'
import {
  getObradaSmjeneStatus,
  ObradaSmjeneStatus,
  pokreniObradaSmjene,
  zatvoriObradaSmjene,
} from '../../../api/thermoboardNalozi'
import UposlenikDropdown from '../../../components/UposlenikDropdown/UposlenikDropdown'
import {getAuth} from '../../../modules/auth'

type FeedbackType = 'success' | 'danger' | 'warning'

const defaultStatus: ObradaSmjeneStatus = {
  mozePokrenutiSmjenu: false,
  trebaZatvoritiSmjenu: false,
  jeVodjaSmjene: false,
  jeUposlenikSmjene: false,
  aktivnaSmjena: null,
}

const normalizeBoolean = (value: any) => value === true || value === 1 || value === '1'
const getSmjenaId = (smjena: any) => Number(smjena?.id ?? smjena?.Id ?? smjena?.smjena_id ?? 0)

const ObradaTBPloca: React.FC = () => {
  const navigate = useNavigate()
  const auth = getAuth()
  const [status, setStatus] = useState<ObradaSmjeneStatus>(defaultStatus)
  const [selectedUposlenici, setSelectedUposlenici] = useState<string[]>([''])
  const [pauza, setPauza] = useState('')
  const [postojaoKvar, setPostojaoKvar] = useState(false)
  const [vrijemeKvara, setVrijemeKvara] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [feedback, setFeedback] = useState<{type: FeedbackType; message: string} | null>(null)

  const statusLabel = useMemo(() => {
    if (status.mozePokrenutiSmjenu) {
      return 'Smjena nije pokrenuta'
    }

    if (status.aktivnaSmjena) {
      return 'Smjena je aktivna'
    }

    return 'Status smjene'
  }, [status])

  const loadStatus = async () => {
    setLoading(true)
    setFeedback(null)

    try {
      const response = await getObradaSmjeneStatus()

      setStatus({
        mozePokrenutiSmjenu: normalizeBoolean(response?.mozePokrenutiSmjenu),
        trebaZatvoritiSmjenu: normalizeBoolean(response?.trebaZatvoritiSmjenu),
        jeVodjaSmjene: normalizeBoolean(response?.jeVodjaSmjene),
        jeUposlenikSmjene: normalizeBoolean(response?.jeUposlenikSmjene),
        aktivnaSmjena: response?.aktivnaSmjena ?? null,
      })
    } catch (error: any) {
      console.error('Error loading Thermoboard shift status:', error)
      setFeedback({
        type: 'danger',
        message: error?.response?.data?.message || error?.message || 'Nije moguce ucitati status smjene.',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStatus()
  }, [])

  const selectedUposleniciIds = selectedUposlenici
    .filter(Boolean)
    .map((uposlenikId) => Number(uposlenikId))
    .filter((uposlenikId) => !Number.isNaN(uposlenikId))

  const handlePocetak = async (event: React.FormEvent) => {
    event.preventDefault()
    setFeedback(null)

    if (selectedUposleniciIds.length === 0) {
      setFeedback({type: 'danger', message: 'Odaberite najmanje jednog uposlenika.'})
      return
    }

    if (new Set(selectedUposleniciIds).size !== selectedUposleniciIds.length) {
      setFeedback({type: 'danger', message: 'Isti uposlenik ne moze biti odabran vise puta.'})
      return
    }

    setSubmitting(true)

    try {
      const response = await pokreniObradaSmjene({
        uposlenici: selectedUposleniciIds,
      })

      const isCreatedShift = response?.id && response?.vrijeme_pocetka

      if (response?.error === 0 || response?.success === true || isCreatedShift) {
        setSelectedUposlenici([''])
        setFeedback({type: 'success', message: 'Uspjesno ste kreirali zapis.'})
        setTimeout(() => navigate(-1), 1200)
        return
      }

      setFeedback({type: 'danger', message: response?.message || 'Smjena nije pokrenuta.'})
    } catch (error: any) {
      console.error('Error starting shift:', error)
      setFeedback({
        type: 'danger',
        message: error?.response?.data?.message || error?.message || 'Doslo je do greske.',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleUposlenikChange = (index: number, value: any) => {
    setFeedback(null)
    setSelectedUposlenici((current) =>
      current.map((uposlenikId, currentIndex) =>
        currentIndex === index ? (value?.toString() ?? '') : uposlenikId
      )
    )
  }

  const addUposlenikRow = () => {
    setSelectedUposlenici((current) => [...current, ''])
  }

  const removeUposlenikRow = (index: number) => {
    setSelectedUposlenici((current) => {
      const next = current.filter((_, currentIndex) => currentIndex !== index)
      return next.length > 0 ? next : ['']
    })
  }

  const handleKraj = async (event: React.FormEvent) => {
    event.preventDefault()
    setFeedback(null)

    const pauzaMinuta = Number(pauza)
    const vrijemeKvaraMinuta = Number(vrijemeKvara)

    if (!pauza || Number.isNaN(pauzaMinuta) || pauzaMinuta < 0) {
      setFeedback({type: 'danger', message: 'Unesite broj minuta pauze.'})
      return
    }

    if (
      postojaoKvar &&
      (!vrijemeKvara || Number.isNaN(vrijemeKvaraMinuta) || vrijemeKvaraMinuta < 0)
    ) {
      setFeedback({type: 'danger', message: 'Unesite broj minuta kvara na postrojenju.'})
      return
    }

    const smjenaId = getSmjenaId(status.aktivnaSmjena)

    if (!smjenaId) {
      setFeedback({type: 'danger', message: 'Nije pronadjena aktivna smjena za zatvaranje.'})
      return
    }

    setSubmitting(true)

    try {
      const response = await zatvoriObradaSmjene({
        id: smjenaId,
        pauza_minuta: pauzaMinuta,
        pauza_kvar: postojaoKvar ? vrijemeKvaraMinuta : null,
      })

      const isClosedShift = response?.id && response?.vrijeme_zavrsetka

      if (response?.error === 0 || response?.success === true || isClosedShift) {
        setPauza('')
        setPostojaoKvar(false)
        setVrijemeKvara('')
        setFeedback({type: 'success', message: response?.message || 'Uspjesno ste zatvorili smjenu.'})
        setTimeout(() => navigate(-1), 1200)
        return
      }

      setFeedback({type: 'danger', message: response?.message || 'Smjena nije zatvorena.'})
    } catch (error: any) {
      console.error('Error closing shift:', error)
      setFeedback({
        type: 'danger',
        message: error?.response?.data?.message || error?.message || 'Doslo je do greske.',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className='container-fluid px-3 px-md-6 py-4'>
      <div className='card shadow-sm border-0'>
        <div className='card-header border-0 pt-6 pb-3'>
          <div className='card-title flex-column align-items-start m-0'>
            <div className='d-flex align-items-center gap-3'>
              <div className='symbol symbol-45px'>
                <div className='symbol-label bg-light-warning'>
                  <KTIcon iconName='timer' className='fs-2 text-warning' />
                </div>
              </div>
              <div>
                <h1 className='fs-3 fw-bold text-gray-900 mb-1'>Obrada TB ploca</h1>
                <div className='text-muted fw-semibold fs-7'>Pocetak i kraj smjene za Thermoboard ploce.</div>
              </div>
            </div>
          </div>
        </div>

        <div className='card-body border-top p-4 p-md-8'>
          {feedback && (
            <div className={`alert alert-${feedback.type} d-flex align-items-center p-5 mb-6`} role='alert'>
              <KTIcon
                iconName={feedback.type === 'success' ? 'check-circle' : 'information-5'}
                className='fs-2hx me-4'
              />
              <div className='fw-bold fs-5'>{feedback.message}</div>
            </div>
          )}

          {loading ? (
            <div className='d-flex align-items-center gap-3 fw-bold text-muted'>
              <span className='spinner-border spinner-border-sm' />
              Ucitavanje statusa smjene...
            </div>
          ) : (
            <>
              <div className='rounded bg-light p-4 p-md-6 mb-6 d-flex flex-column flex-lg-row justify-content-between gap-4'>
                <div>
                  <div className='fw-bold text-gray-900 fs-5 mb-1'>{statusLabel}</div>
                  <div className='text-muted fw-semibold'>
                    Vodja smjene: {status.jeVodjaSmjene ? 'Da' : 'Ne'} | Uposlenik smjene:{' '}
                    {status.jeUposlenikSmjene ? 'Da' : 'Ne'}
                  </div>
                </div>

                <button type='button' className='btn btn-light fw-bold align-self-start' onClick={loadStatus}>
                  <KTIcon iconName='arrows-circle' className='fs-3' />
                  Osvjezi
                </button>
              </div>

              {status.mozePokrenutiSmjenu ? (
                <form onSubmit={handlePocetak} noValidate>
                  <div className='mb-6'>
                    <div className='d-flex align-items-center gap-3 mb-4'>
                      <span className='badge badge-light-success fs-6 p-3'>Pocetak</span>
                      <div className='fw-bold fs-4 text-gray-900'>Pokretanje smjene</div>
                    </div>

                    <div className='rounded border border-dashed p-4 mb-5'>
                      <div className='text-muted fw-semibold fs-7 mb-1'>Vodja smjene</div>
                      <div className='fw-bold fs-5 text-gray-900'>
                        {auth?.UposlenikImePrezime || auth?.Username || 'Prijavljeni korisnik'}
                      </div>
                      {auth?.UposlenikId && (
                        <div className='text-muted fw-semibold fs-8 mt-1'>ID: {auth.UposlenikId}</div>
                      )}
                    </div>

                    <label className='form-label required fw-bold fs-6'>Uposlenici</label>
                    <div className='d-flex flex-column gap-3'>
                      {selectedUposlenici.map((uposlenikId, index) => (
                        <div className='row g-3 align-items-center' key={index}>
                          <div className='col-12 col-md'>
                            <UposlenikDropdown
                              value={uposlenikId}
                              onChange={(_, value) => handleUposlenikChange(index, value)}
                              selectName={`uposlenik_${index}`}
                            />
                          </div>

                          <div className='col-12 col-md-auto d-flex gap-2'>
                            <button
                              type='button'
                              className='btn btn-light-success fw-bold'
                              onClick={addUposlenikRow}
                              disabled={submitting}
                            >
                              <KTIcon iconName='plus' className='fs-3' />
                            </button>

                            <button
                              type='button'
                              className='btn btn-light-danger fw-bold'
                              onClick={() => removeUposlenikRow(index)}
                              disabled={submitting || selectedUposlenici.length === 1}
                            >
                              <KTIcon iconName='minus' className='fs-3' />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className='d-flex justify-content-end'>
                    <button type='submit' className='btn btn-success fw-bold px-10 py-4' disabled={submitting}>
                      {submitting ? (
                        <>
                          <span className='spinner-border spinner-border-sm me-2' />
                          Obrada...
                        </>
                      ) : (
                        'Pocetak'
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleKraj} noValidate>
                  <div className='mb-6'>
                    <div className='d-flex align-items-center gap-3 mb-4'>
                      <span className='badge badge-light-danger fs-6 p-3'>Kraj</span>
                      <div className='fw-bold fs-4 text-gray-900'>Zatvaranje smjene</div>
                    </div>

                    <div className='row g-5'>
                      <div className='col-12 col-md-4'>
                        <label className='form-label required fw-bold fs-6'>Pauza (min)</label>
                        <input
                          type='number'
                          min='0'
                          step='1'
                          inputMode='numeric'
                          className='form-control form-control-lg form-control-solid'
                          value={pauza}
                          onChange={(event) => setPauza(event.target.value)}
                          placeholder='Unesite broj minuta'
                          disabled={submitting || !status.trebaZatvoritiSmjenu}
                        />
                      </div>

                      <div className='col-12 col-md-4'>
                        <label className='form-label required fw-bold fs-6'>Postojao kvar na postrojenju</label>
                        <select
                          className='form-select form-select-lg form-select-solid'
                          value={postojaoKvar ? '1' : '0'}
                          onChange={(event) => {
                            const hasBreakdown = event.target.value === '1'
                            setPostojaoKvar(hasBreakdown)
                            if (!hasBreakdown) {
                              setVrijemeKvara('')
                            }
                          }}
                          disabled={submitting || !status.trebaZatvoritiSmjenu}
                        >
                          <option value='0'>Ne</option>
                          <option value='1'>Da</option>
                        </select>
                      </div>

                      {postojaoKvar && (
                        <div className='col-12 col-md-4'>
                          <label className='form-label required fw-bold fs-6'>Vrijeme kvara (min)</label>
                          <input
                            type='number'
                            min='0'
                            step='1'
                            inputMode='numeric'
                            className='form-control form-control-lg form-control-solid'
                            value={vrijemeKvara}
                            onChange={(event) => setVrijemeKvara(event.target.value)}
                            placeholder='Unesite broj minuta'
                            disabled={submitting || !status.trebaZatvoritiSmjenu}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {!status.trebaZatvoritiSmjenu && (
                    <div className='alert alert-warning fw-bold'>
                      Nemate aktivnu smjenu za zatvaranje ili niste vodja smjene.
                    </div>
                  )}

                  <div className='d-flex justify-content-end'>
                    <button
                      type='submit'
                      className='btn btn-danger fw-bold px-10 py-4'
                      disabled={submitting || !status.trebaZatvoritiSmjenu}
                    >
                      {submitting ? (
                        <>
                          <span className='spinner-border spinner-border-sm me-2' />
                          Obrada...
                        </>
                      ) : (
                        'Kraj'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ObradaTBPloca
